/**
 * IN WELCHEM RAUM STEHT EIN CHAT OHNE BUEHNE? (Stand 21.09.2026: IMMER IM SITZUNGSSAAL — seit 09.10. siehe unten.)
 *
 * Rafael, 21.09.2026, und er musste es zum wiederholten Mal sagen:
 *   *"Warum gibt es im Pausenraum Jets ohne Bühne? Das sollte es ja wiederum nicht
 *   geben, sondern Jets ohne Bühne gehören in den Sitzungssaal."*
 *   *"Meine Regelung ist so einfach: Ich habe es schon 100, 150 Millionen mal gesagt.
 *   Eine Sitzung ist so lange im Sitzungssaal, bis sie eine Bühne hat und dann kommen
 *   sie zurück in den Warteraum. Vom Warteraum aus können sie in den Pausenraum
 *   verschoben werden. Sonst gibt es da keine Wege."*
 *
 * WARUM DAS HIER STEHT UND NICHT MEHR IM JSX: es war eine einzige Bedingung mitten in
 * der Zuweisung an <RaumKasten>, und sie hat 14 Stunden lang zwei Chats in den
 * Pausenraum gestellt, ohne dass es jemandem auffiel. Eine Regel, die Rafael mehrfach
 * aussprechen musste, gehoert an eine Stelle, die man suchen und pruefen kann.
 *
 * WAS VORHER HIER STAND und warum es kein Alleingang war: die alte Fassung schickte
 * einen Chat ohne Buehne in den Pausenraum, sobald er als `tot` gemessen wurde. Das
 * folgte Rafaels EIGENER aelterer Anweisung (13.09.2026: *"Tote chats muessen in den
 * Pausenraum"*, Regel 3 in refs/gemeinsam/hofbetrieb.md). Die neue Anweisung schlaegt
 * die alte; die Doku ist nachgezogen (Commit 9b0805d).
 *
 * `tot` ENTSCHEIDET HIER GAR NICHTS MEHR — und das ist der Kern. Serverseitig heisst es
 * nur `processAlive === false` bei OFFENER Sitzung. Beide am 21.09. 07:21Z gemessenen
 * Faelle trugen als Zustand woertlich *"Sitzung ruht seit 16:49Z — kann mit der
 * nächsten Nachricht fortgesetzt werden"*. Das ist ein weckbarer Chat, kein Endzustand;
 * die Zeile sagt deshalb "ruht, weckbar".
 */
/*
 * SEIT 09.10.2026 IN KEINEM RAUM MEHR, SONDERN GANZ OBEN (Rafael, Bühne
 * b-guide-jede-sitzung-ein-platz-20261009: „es soll eigentlich nur 3 modi geben - wartet auf dich,
 * in arbeit und pausenraum"). Eine Sitzung ohne Platz ist ein Defekt und steht laut über „Wartet auf
 * dich", nicht still im Sitzungssaal mitgezählt. Der Raum der Regel vom 21.09. ist damit keiner
 * mehr — was von ihr bleibt: nie im Pausenraum, nie wegsortiert, `tot` entscheidet nichts.
 */
export function chatsOhneBuehneImRaum<T>(_raum: string, _chats: T[]): T[] {
  return [];
}

/** Die Raeume, in denen NIE ein Chat ohne Buehne stehen darf — als pruefbare Aussage,
 *  nicht als Kommentar. Seit 09.10.2026 alle: der Platz-Block steht ueber den Raeumen. */
export const RAEUME_OHNE_CHATS = ['vorraum', 'warteraum', 'sitzungssaal', 'pausenraum', 'vor_dem_koenig'] as const;

/** Der Titel des Platz-Blocks — oder null, dann steht der Block nicht da (nur bei N > 0). */
export function platzTitel(n: number): string | null {
  if (n <= 0) return null;
  return n === 1 ? '1 Sitzung ohne Platz' : `${n} Sitzungen ohne Platz`;
}

/** Ergebnis eines Knopfs an der Zeile — immer sichtbar, nie still. */
export type PlatzAktionErgebnis = { ok: true; satz: string } | { ok: false; satz: string };

async function fehlertext(r: Response): Promise<string> {
  const d = await r.json().catch(() => null) as { error?: string; message?: string } | null;
  return `HTTP ${r.status}${d?.message || d?.error ? ` — ${d.message || d.error}` : ''}`;
}

/**
 * „beenden" — der bestehende Weg (`POST /api/mission/conversation/:id/finish`). Eine Sitzung ohne
 * Bühne ist beendbar; Rafaels Klick traegt `x-cui-eingabe: mensch` wie der Finish-Knopf im Chat.
 */
export async function beendeOhnePlatz(sid: string, f: typeof fetch = fetch): Promise<PlatzAktionErgebnis> {
  try {
    const r = await f(`/api/mission/conversation/${encodeURIComponent(sid)}/finish`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'x-cui-eingabe': 'mensch' },
      body: JSON.stringify({ finished: true, confirm: true }), signal: AbortSignal.timeout(30000),
    });
    if (!r.ok) return { ok: false, satz: `Nicht beendet: ${await fehlertext(r)}` };
    return { ok: true, satz: r.status === 202 ? 'Beendet — der Prozess hält nach seinem Zug an.' : 'Beendet.' };
  } catch (e) { return { ok: false, satz: `Nicht beendet: ${e instanceof Error ? e.message : String(e)}` }; }
}

/** „Bühne daraus machen" — die Sitzung bekommt die Aufforderung, ihre Bühne selbst anzulegen. */
export async function fordereBuehneAn(sid: string, f: typeof fetch = fetch): Promise<PlatzAktionErgebnis> {
  try {
    const r = await f(`/api/guide/platz/${encodeURIComponent(sid)}/buehne-anfordern`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: AbortSignal.timeout(90000),
    });
    if (!r.ok) return { ok: false, satz: `Nicht zugestellt: ${await fehlertext(r)}` };
    const d = await r.json().catch(() => ({})) as { vorschlag?: { id?: string }; art?: string };
    if (d.art === 'erneut-zuordnen') return { ok: true, satz: 'Zugestellt — beim Fortsetzen versucht der Server die Bühnenzuordnung erneut.' };
    return { ok: true, satz: `Aufforderung zugestellt${d.vorschlag?.id ? ` (Vorschlag ${d.vorschlag.id})` : ''} — die Sitzung legt ihre Bühne selbst an.` };
  } catch (e) { return { ok: false, satz: `Nicht zugestellt: ${e instanceof Error ? e.message : String(e)}` }; }
}

/** Ein Vermerk „beendet ohne Ergebnis-Nachweis" (Server: lib/beendet-ohne-ergebnis.ts, Baustein 6). */
export interface BeendetOhneErgebnis { sitzung: string; titel: string | null; am: string; von: string; haus: 'dev' | 'partner'; grund?: string; fehler?: boolean; erinnert?: number }

/** Liest die offenen Vermerke BEIDER Häuser (Zweitprüfung 1, Befund 4c). Ein unerreichbares Haus steht
 *  in `fehler`, nie als leere Liste. Wirft laut, wenn dieses Haus selbst nicht antwortet. */
export async function ladeBeendetOhneErgebnis(f: typeof fetch = fetch): Promise<{ eintraege: BeendetOhneErgebnis[]; fehler: Record<string, string> }> {
  const r = await f('/api/guide/platz-hier/beendet-ohne-ergebnis/alle', { signal: AbortSignal.timeout(15000) });
  if (!r.ok) throw new Error(`„Beendet ohne Ergebnis-Nachweis" nicht lesbar: ${await fehlertext(r)}`);
  const d = await r.json() as { eintraege?: BeendetOhneErgebnis[]; fehler?: Record<string, string> };
  if (!Array.isArray(d.eintraege)) throw new Error('„Beendet ohne Ergebnis-Nachweis": Antwort ohne Liste.');
  return { eintraege: d.eintraege, fehler: d.fehler ?? {} };
}

/** Wegklicken — der Server des Hauses, in dem beendet wurde, vermerkt wer/wann; nichts wird gelöscht. */
export async function klickeBeendetWeg(haus: string, sid: string, f: typeof fetch = fetch): Promise<PlatzAktionErgebnis> {
  try {
    const r = await f(`/api/guide/platz-hier/beendet-ohne-ergebnis/${encodeURIComponent(haus)}/${encodeURIComponent(sid)}/weg`, { method: 'POST', signal: AbortSignal.timeout(15000) });
    return r.ok ? { ok: true, satz: 'Weggeklickt.' } : { ok: false, satz: `Nicht weggeklickt: ${await fehlertext(r)}` };
  } catch (e) { return { ok: false, satz: `Nicht weggeklickt: ${e instanceof Error ? e.message : String(e)}` }; }
}

/** Baustein 8: eine Bühne, deren Berater-Start scheiterte (Server `buehnen_start_gescheitert`). */
export interface StartGescheitert { buehne: string; titel: string; fehler: string; am: string; workspace: string | null; thema: string; auftrag: string }

/** „erneut starten": derselbe Weg wie der Knopf „Berater einsetzen", mit der bestehenden Bühne. */
export async function starteBuehneErneut(b: StartGescheitert, f: typeof fetch = fetch): Promise<PlatzAktionErgebnis> {
  if (!b.workspace) return { ok: false, satz: 'Nicht gestartet: die Bühne hat keinen Arbeitsbereich.' };
  try {
    const r = await f('/api/guide/buehnen/berater-einsetzen', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: AbortSignal.timeout(150000),
      body: JSON.stringify({ von: 'rafael', buehne: b.buehne, workspace: b.workspace, thema: b.thema, auftrag: b.auftrag }),
    });
    if (!r.ok) return { ok: false, satz: `Nicht gestartet: ${await fehlertext(r)}` };
    const d = await r.json().catch(() => ({})) as { sitzung?: string };
    return { ok: true, satz: `Berater gestartet${d.sitzung ? ` (${d.sitzung.slice(0, 8)})` : ''}.` };
  } catch (e) { return { ok: false, satz: `Nicht gestartet: ${e instanceof Error ? e.message : String(e)}` }; }
}
